import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link as MuiLink,
  Stack,
  Text,
  VStack,
  Spinner,
} from "@chakra-ui/react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Language } from "../../models/Language";
import useGetLanguages from "../../hooks/api/learning-path/useGetLanguages";

export type SignUpFormValues = {
  name: string;
  surname: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  languages: Language[];
};

type Props = {
  onSubmit: (values: SignUpFormValues) => void;
  errorMessage?: string;
  isLoading?: boolean;
};

export default function SignUpForm({
  onSubmit,
  isLoading,
  errorMessage,
}: Props) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    getValues,
    formState: { errors, isValid, isSubmitted },
  } = useForm<SignUpFormValues>();
  const { append, remove, fields } = useFieldArray({
    control,
    name: "languages",
  });
  const { data: languages, isPending: areLanguagesLoading } = useGetLanguages();

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl isInvalid={!isValid && isSubmitted}>
        <Box flexGrow={1} mb={4} display="flex" flexDirection="column">
          <FormLabel htmlFor="name">{t("forms.name")}</FormLabel>
          <Input
            id="name"
            step="1"
            placeholder={t("forms.name")}
            {...register("name", {
              required: t("forms.validation.required"),
            })}
          />
          <FormErrorMessage>
            {errors.name && errors.name.message}
          </FormErrorMessage>
        </Box>
        <Box flexGrow={1} mb={4} display="flex" flexDirection="column">
          <FormLabel htmlFor="surname">{t("forms.surname")}</FormLabel>
          <Input
            id="surname"
            step="1"
            placeholder={t("forms.surname")}
            {...register("surname", {
              required: t("forms.validation.required"),
            })}
          />
          <FormErrorMessage>
            {errors.surname && errors.surname.message}
          </FormErrorMessage>
        </Box>
        <Box flexGrow={1} mb={4} display="flex" flexDirection="column">
          <FormLabel htmlFor="email">{t("forms.email")}</FormLabel>
          <Input
            id="email"
            step="1"
            autoComplete="email"
            placeholder="Email"
            {...register("email", {
              required: t("forms.validation.required"),
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@sapienza\.it$/,
                message: t("forms.validation.invalid_format"),
              },
            })}
          />
          <Text mx={1} mt={2} fontSize={12} fontStyle="italic" color="gray.500">
            {t("sign_up_page.institutional_email")}
          </Text>
          <FormErrorMessage>
            {errors.email && errors.email.message}
          </FormErrorMessage>
        </Box>
        <Box flexGrow={1} mb={4}>
          <FormLabel htmlFor="password">{t("forms.password")}</FormLabel>
          <Input
            id="password"
            type="password"
            step="1"
            autoComplete="new-password"
            placeholder="Password"
            {...register("password", {
              required: t("forms.validation.required"),
              pattern: {
                value:
                  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                message: t("forms.validation.invalid_format"),
              },
            })}
          />
          <Text mx={1} mt={2} fontSize={12} fontStyle="italic" color="gray.500">
            {t("sign_up_page.password_requirements")}
          </Text>
          <FormErrorMessage>
            {errors.password && errors.password.message}
          </FormErrorMessage>
        </Box>
        <Box flexGrow={1} mb={4}>
          <FormLabel htmlFor="passwordConfirmation">
            {t("forms.password_confirmation")}
          </FormLabel>
          <Input
            id="passwordConfirmation"
            type="password"
            step="1"
            autoComplete="new-password"
            placeholder={t("forms.password_confirmation")}
            {...register("passwordConfirmation", {
              required: t("forms.validation.required"),
              validate: (value) =>
                value !== getValues("password")
                  ? t("forms.validation.must_be_equal", {
                      param1: t("forms.password").toLowerCase(),
                      param2: t("forms.password_confirmation").toLowerCase(),
                    })
                  : undefined,
            })}
          />
          <FormErrorMessage>
            {errors.passwordConfirmation && errors.passwordConfirmation.message}
          </FormErrorMessage>
        </Box>
        <Box flexGrow={1} mb={4}>
          <FormLabel htmlFor="password">{t("forms.languages")}</FormLabel>
          <CheckboxGroup>
            <Stack mt={4}>
              {languages?.content.length ? (
                languages?.content.map((language) => (
                  <Checkbox
                    id={`languagesId.${language.id}`}
                    key={language.id}
                    onChange={(e) =>
                      e.target.checked
                        ? append(language)
                        : remove(
                            fields
                              .map(({ name }) => name)
                              .indexOf(language.name),
                          )
                    }
                  >
                    {language.name}
                  </Checkbox>
                ))
              ) : (
                <Spinner color="primary.DEFAULT" />
              )}
            </Stack>
          </CheckboxGroup>
          <FormErrorMessage>
            {errors.password && errors.password.message}
          </FormErrorMessage>
        </Box>
        <Text color="red">{errorMessage}</Text>
        <Box justifyContent="flex-end" display="flex" flexGrow={1} mx={2}>
          <MuiLink as={Link} to="/" textAlign="right" color={"primary.DEFAULT"}>
            {t("sign_up_page.login")}
          </MuiLink>
        </Box>
        <VStack>
          <Button
            alignSelf="stretch"
            mt={4}
            bg="primary.DEFAULT"
            color="white"
            type="submit"
            disabled={areLanguagesLoading}
            isLoading={isLoading}
          >
            {t("common.buttons.sign_up")}
          </Button>
        </VStack>
      </FormControl>
    </form>
  );
}
